'use client';

import { useRouter as useCompatRouter } from 'next/compat/router';
import {
  useRouter as useNavigationRouter,
  useParams,
  usePathname,
} from 'next/navigation';
import type { NextRouter } from 'next/router';
import type { ParsedUrlQuery } from 'querystring';
import { useMemo, useSyncExternalStore } from 'react';

const SUPPORTED_LOCALES = new Set(['en', 'ko']);
const routerEvents: NextRouter['events'] = {
  emit: () => undefined,
  off: () => undefined,
  on: () => undefined,
};

type Route = Parameters<NextRouter['push']>[0];
type TransitionOptions = Parameters<NextRouter['push']>[2];
const LOCATION_CHANGE_EVENT = 'app-router-location-change';

function subscribeToLocation(callback: () => void) {
  window.addEventListener('popstate', callback);
  window.addEventListener(LOCATION_CHANGE_EVENT, callback);
  return () => {
    window.removeEventListener('popstate', callback);
    window.removeEventListener(LOCATION_CHANGE_EVENT, callback);
  };
}

function getLocationSearch() {
  return window.location.search;
}

function getServerLocationSearch() {
  return '';
}

export function useLocationSearch() {
  return useSyncExternalStore(
    subscribeToLocation,
    getLocationSearch,
    getServerLocationSearch,
  );
}

function normalizeQueryValue(value: unknown): string | string[] | undefined {
  if (typeof value === 'string') return value;
  if (Array.isArray(value) && value.every((item) => typeof item === 'string')) {
    return value;
  }
  return undefined;
}

export function routeToString(route: Route) {
  if (typeof route === 'string') return route;

  const pathname = route.pathname ?? '/';
  const searchParams = new URLSearchParams();

  Object.entries(route.query ?? {}).forEach(([key, rawValue]) => {
    const value = normalizeQueryValue(rawValue);
    if (Array.isArray(value)) {
      value.forEach((item) => searchParams.append(key, item));
    } else if (value !== undefined) {
      searchParams.set(key, value);
    }
  });

  const query = searchParams.toString();
  return `${pathname}${query ? `?${query}` : ''}${route.hash ?? ''}`;
}

export function withLocale(href: string, locale?: string | false) {
  if (!locale || !SUPPORTED_LOCALES.has(locale)) return href;

  const url = new URL(href, 'https://deeplyinc.com');
  const pathWithoutLocale =
    url.pathname.replace(/^\/(?:en|ko)(?=\/|$)/, '') || '/';
  url.pathname =
    locale === 'en' ? pathWithoutLocale : `/ko${pathWithoutLocale}`;
  return `${url.pathname}${url.search}${url.hash}`;
}

function createNavigation(
  action: 'push' | 'replace',
  navigationRouter: ReturnType<typeof useNavigationRouter>,
  currentLocale: string,
) {
  return async (
    route: Route,
    as?: Route,
    options?: TransitionOptions,
  ): Promise<boolean> => {
    const href = withLocale(
      routeToString(as ?? route),
      options?.locale ?? currentLocale,
    );
    navigationRouter[action](href, { scroll: options?.scroll });
    window.requestAnimationFrame(() => {
      window.dispatchEvent(new Event(LOCATION_CHANGE_EVENT));
    });
    return true;
  };
}

export function useRouter(): NextRouter {
  const pagesRouter = useCompatRouter();
  const navigationRouter = useNavigationRouter();
  const pathname = usePathname() ?? '/';
  const search = useLocationSearch();
  const params = useParams();

  return useMemo(() => {
    if (pagesRouter) return pagesRouter;

    const localeParam = params?.lng;
    const locale = typeof localeParam === 'string' ? localeParam : 'en';
    const routePath = pathname.replace(/^\/(?:en|ko)(?=\/|$)/, '') || '/';
    const query: ParsedUrlQuery = Object.fromEntries(
      new URLSearchParams(search).entries(),
    );

    Object.entries(params ?? {}).forEach(([key, value]) => {
      if (key !== 'lng' && value !== undefined) query[key] = value;
    });

    const asPath = `${pathname}${search}`;

    return {
      asPath,
      back: () => navigationRouter.back(),
      basePath: '',
      beforePopState: () => undefined,
      defaultLocale: 'en',
      domainLocales: undefined,
      events: routerEvents,
      forward: () => navigationRouter.forward(),
      isFallback: false,
      isLocaleDomain: false,
      isPreview: false,
      isReady: true,
      locale,
      locales: ['en', 'ko'],
      pathname: routePath,
      prefetch: async (url) => navigationRouter.prefetch(routeToString(url)),
      push: createNavigation('push', navigationRouter, locale),
      query,
      reload: () => window.location.reload(),
      replace: createNavigation('replace', navigationRouter, locale),
      route: routePath,
    };
  }, [navigationRouter, pagesRouter, params, pathname, search]);
}
